import {
  CircleDollarSign,
  ClipboardCheck,
  FilePenLine,
  Gauge,
  GraduationCap,
  Handshake,
  Link2,
  LogOut,
  MessageSquareText,
  LayoutGrid,
  Plus,
  Search,
  Send,
  Settings as SettingsIcon,
  ShieldCheck,
  Upload,
  UsersRound,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useUser } from "../lib/auth";
import { NotificationsBell } from "./NotificationsBell";

// Resolve the public portal hostname at render time. If the staff app is
// served from app.<root>, the portal is at portal.<root>. If we're on
// localhost or a workers.dev fallback host we don't have a sibling portal
// subdomain, so the Client Portal link falls back to /portal/login on the
// same origin (the staff bundle keeps a defensive /portal route mounted).
function resolvePortalHref(): string {
  if (typeof window === "undefined") return "/portal/login";
  const host = window.location.hostname;
  const m = host.match(/^app\.(.+)$/i);
  if (m) return `${window.location.protocol}//portal.${m[1]}/portal/login`;
  // Apex / workers.dev / localhost — stay on this origin.
  return "/portal/login";
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  external?: boolean;
}

const navItems: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge },
  { to: "/clients", label: "Clients", icon: UsersRound },
  { to: "/reports", label: "Credit Reports", icon: Upload },
  { to: "/disputes", label: "Disputes", icon: ClipboardCheck },
  { to: "/letters", label: "Letters", icon: FilePenLine },
  { to: "/mail", label: "Letter Mailing", icon: Send },
  // Client Portal opens in a new tab on the portal subdomain. Staff are NOT
  // logged into the portal — this link is for them to preview what clients
  // see. Using an external anchor here (instead of a same-bundle <NavLink>)
  // avoids the previous bug where /portal redirected to /dashboard.
  { to: "__portal__", label: "Client Portal", icon: MessageSquareText, external: true },
  { to: "/billing", label: "Billing", icon: CircleDollarSign },
  { to: "/affiliates", label: "Affiliates", icon: Handshake },
  { to: "/integrations", label: "Integrations", icon: Link2 },
  { to: "/compliance", label: "Online Dispute", icon: ShieldCheck },
  { to: "/training", label: "Training", icon: GraduationCap },
  { to: "/settings", label: "Company Settings", icon: SettingsIcon },
];

export function Shell({
  children,
  title,
  subtitle,
  actions,
}: {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { user, logout } = useUser();
  const nav = useNavigate();

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <div className="brand-mark">
            <img src="/brand-logo.png" alt="No Excuses" />
          </div>
          <div>
            <strong className="brand-wordmark">No Excuses</strong>
            <span>Dispute Manager</span>
          </div>
        </div>

        <nav>
          {navItems.map((item) => {
            const Icon = item.icon;
            if (item.external) {
              const href = item.label === "Client Portal" ? resolvePortalHref() : item.to;
              return (
                <a
                  key={item.label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${item.label} (opens in a new tab)`}
                >
                  <Icon size={18} aria-hidden="true" />
                  <span>{item.label}</span>
                </a>
              );
            }
            return (
              // NavLink in react-router-dom v6+ sets aria-current="page" on the
              // active link by default — we rely on that for screen readers.
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => (isActive ? "active" : undefined)}
                title={item.label}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          {/* Platform owner only — the master console across all companies. */}
          {user?.is_super_admin && (
            <NavLink to="/admin" className={({ isActive }) => (isActive ? "active" : undefined)} title="Master Console">
              <LayoutGrid size={18} aria-hidden="true" />
              <span>Master Console</span>
            </NavLink>
          )}
        </nav>

        <div className="sidebar-panel">
          <p>Signed in</p>
          <strong>{user?.name || "—"}</strong>
          <span>{user?.email}</span>
          <button
            type="button"
            className="sidebar-logout"
            onClick={async () => { await logout(); nav("/login"); }}
          >
            <LogOut size={14} aria-hidden="true" /> Sign out
          </button>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <p>{subtitle || "Credit repair CRM"}</p>
            <h1>{title}</h1>
          </div>
          <div className="topbar-actions">
            <label className="search">
              <Search size={18} aria-hidden="true" />
              <input
                placeholder="Search clients, disputes, letters"
                aria-label="Search clients, disputes, letters"
              />
            </label>
            <NotificationsBell />
            {actions ?? (
              <button className="primary-button" onClick={() => nav("/clients/new")}>
                <Plus size={18} aria-hidden="true" /> New Client
              </button>
            )}
          </div>
        </header>

        {children}
      </section>
    </main>
  );
}
